
import { useTranslation } from "react-i18next";
import OptimizedImage from '@/components/ui/optimized-image';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const languages = [
  { code: "en", label: "English", flag: "/lovable-uploads/uk-flag.webp", alt: "UK flag" },
  { code: "fr", label: "Français", flag: "/lovable-uploads/419078fa-a6b5-430f-bf90-2c87377fabe5.png", alt: "French flag" },
  { code: "pt-BR", label: "Português", flag: "/lovable-uploads/0571497e-358a-4002-91c7-196f6fbc3702.png", alt: "Brazilian flag" }
];

const LanguageToggle = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLang) || languages[0];
  };
  
  const currentLanguage = getCurrentLanguage();
  
  const changeLang = (langCode: string) => {
    console.log("LanguageToggle: Changing language to:", langCode);
    i18n.changeLanguage(langCode);
    localStorage.setItem("language", langCode);
    console.log("LanguageToggle: localStorage set to:", localStorage.getItem("language"));
    console.log("LanguageToggle: i18n.language is now:", i18n.language);
  };

  return (
    <div className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="group flex items-center gap-2 px-3 py-2 rounded-xl 
                     bg-gradient-to-r from-background/80 to-muted/50 
                     hover:from-background to-muted 
                     border border-border/50 hover:border-border
                     backdrop-blur-sm shadow-sm hover:shadow-md
                     transition-all duration-300 ease-out
                     focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
            aria-label={`${currentLanguage.code.toUpperCase()} - Select language`}
          >
            <div className="relative">
              <div className="w-7 h-4 overflow-hidden rounded-sm border border-border/30 bg-muted/50">
                <OptimizedImage 
                  src={currentLanguage.flag}
                  alt={currentLanguage.alt}
                  width={28}
                  height={13}
                  priority={true}
                  className="w-full h-full object-cover object-center scale-105"
                />
              </div>
            </div>
            <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors duration-200">
              {currentLanguage.code.toUpperCase()}
            </span>
            <ChevronDown 
              size={14} 
              className="text-muted-foreground group-hover:text-foreground 
                       transform group-hover:rotate-180 transition-all duration-300" 
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48 p-2 bg-background/95 backdrop-blur-md border border-border/50 shadow-xl rounded-xl animate-in slide-in-from-top-2 duration-200"
        >
          {languages.map((language) => (
            <DropdownMenuItem
              key={language.code}
              onClick={() => changeLang(language.code)}
              className={`flex items-center gap-3 p-3 cursor-pointer rounded-lg
                        transition-all duration-200 ease-out
                        hover:bg-accent/50 hover:shadow-sm
                        ${currentLanguage.code === language.code ? 'bg-primary/10 border border-primary/20' : ''}
                        focus:bg-accent/50 focus:outline-none`}
            >
              <div className="relative">
                <div className="w-8 h-5 overflow-hidden rounded-sm border border-border/30 bg-muted/30 shadow-sm">
                  <OptimizedImage 
                    src={language.flag}
                    alt={language.alt}
                    width={32}
                    height={15}
                    className="w-full h-full object-cover object-center scale-125"
                  />
                </div>
                {currentLanguage.code === language.code && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full border border-background"></div>
                )}
              </div>
              <span className="text-sm font-medium text-foreground">
                {language.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default LanguageToggle;
